import { useMemo, useRef, useState } from 'react';
import {
  canPrintInBrowser,
  documentKind,
  DOCUMENT_RULES,
  fileSizeLabel,
  isWordDocument,
  sortDocuments,
  type TeacherDocument,
} from '@td/shared';
import {
  documentDownloadUrl,
  documentUrl,
  useDeleteDocument,
  useDocumentHtml,
  useDocuments,
  useUpdateDocument,
  useUploadDocument,
} from '../hooks/useDocuments';
import { DocumentThumbnail, PdfReader, WordReader } from '../components/DocumentView';
import { printDocuments, type Printable } from '../lib/printFile';
import { HeaderMenu } from '../components/HeaderMenu';
import { Modal } from '../components/Modal';
import { useConfirm } from '../components/ConfirmDialog';
import { Doodle } from '../components/Doodle';

/**
 * Documents.
 *
 * A wall of cards, each showing the top of the document itself. A filing
 * cabinet is recognised by sight — "the one with the table on it" — long
 * before anybody reads the label, and the first version of this page was a
 * list of text fields that made her read every name to find anything.
 *
 * So the card is the document, and everything you can *do* with it waits
 * until it is opened. Only the star sits on the card, because pinning is the
 * one thing done while scanning rather than after choosing.
 *
 * One honest limitation runs through the page: a browser can render a PDF and
 * an image and cannot render a Word file. Those get a mark and a download,
 * with the reason written on them.
 */
export function DocumentsPage() {
  const [search, setSearch] = useState('');

  /*
   * The search is the query rather than a filter over one.
   *
   * The words inside each document live on the server, so the server is what
   * looks through them — the page asks a question and is handed the answer,
   * with a line saying why each result matched.
   */
  const { data, isLoading, isFetching } = useDocuments(search);
  const documents = useMemo(() => data?.documents ?? [], [data]);
  const upload = useUploadDocument();
  const remove = useDeleteDocument();
  const confirm = useConfirm();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Already filtered by the server; only the order is decided here.
  const shown = useMemo(() => sortDocuments(documents), [documents]);

  // Always the live one, so a rename made in the card shows in the card.
  const open = documents.find((d) => d.id === openId) ?? null;

  const acting = useMemo(
    () => (selected.size > 0 ? shown.filter((d) => selected.has(d.id)) : shown),
    [shown, selected],
  );

  // Word files print through their converted HTML, which is fetched when one
  // is opened — so a bulk print covers PDFs and images, and says as much.
  const printable = acting.filter((d) => canPrintInBrowser(d.contentType));

  function toggle(id: string) {
    setSelected((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;

    // One at a time: several large uploads at once is several simultaneous
    // re-encodes on a small container, and the whole batch fails together.
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync({ file });
      } catch {
        // Keep going — one refused file should not stop the others.
      }
    }

    if (fileInput.current) fileInput.current.value = '';
  }

  const [printing, setPrinting] = useState(false);

  /**
   * One print job, however many documents are in it.
   *
   * The pages are drawn here rather than handed to a plugin, which is what
   * makes several documents one job instead of one dialog each.
   */
  async function print(list: Printable[]) {
    if (list.length === 0) return;

    setPrinting(true);
    try {
      await printDocuments(list.slice(0, DOCUMENT_RULES.maxPrintAtOnce));
    } finally {
      setPrinting(false);
    }
  }

  return (
    <div className="space-y-4">
      <header className="page-header flex flex-wrap items-end justify-between gap-3">
        <h1 className="flex items-baseline gap-2 text-2xl">
          Documents
          {selected.size > 0 && <span className="text-sm text-stone">{selected.size} selected</span>}
        </h1>

        <div className="flex flex-wrap items-center gap-1.5">
          <input
            className="field w-44 py-1.5"
            placeholder="Find anything, including inside…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search the documents and their contents"
          />

          {isFetching && search.trim().length > 0 && (
            <span className="text-[11px] text-stone">Looking…</span>
          )}

          <input
            ref={fileInput}
            type="file"
            accept={DOCUMENT_RULES.acceptedMimeTypes.join(',')}
            multiple
            className="hidden"
            onChange={(e) => void onFiles(e.target.files)}
          />

          <button
            className="btn-primary"
            onClick={() => fileInput.current?.click()}
            disabled={upload.isPending || printing}
          >
            {upload.isPending ? 'Adding…' : printing ? 'Preparing…' : 'Add a document'}
          </button>

          <HeaderMenu label="What to do with these">
            {(close) => {
              const scope = selected.size > 0 ? `the ${selected.size} selected` : 'everything here';

              return (
                <div className="space-y-1.5">
                  <p className="px-1 pb-1 text-[11px] text-stone">
                    {printable.length === 0
                      ? 'Nothing here can be printed from the browser'
                      : `${printable.length} of ${acting.length} can be printed here`}
                  </p>

                  <button
                    className="btn-soft w-full"
                    disabled={printable.length === 0}
                    onClick={() => {
                      close();
                      void print(
                        printable.map((d) => ({ url: documentUrl(d), contentType: d.contentType })),
                      );
                    }}
                  >
                    Print {scope}
                  </button>

                  {selected.size > 0 ? (
                    <button
                      className="btn-soft w-full"
                      onClick={() => {
                        setSelected(new Set());
                        close();
                      }}
                    >
                      Clear the selection
                    </button>
                  ) : (
                    <button
                      className="btn-soft w-full"
                      disabled={shown.length === 0}
                      onClick={() => {
                        setSelected(new Set(shown.map((d) => d.id)));
                        close();
                      }}
                    >
                      Select every one of these
                    </button>
                  )}

                  {selected.size > 0 && (
                    <button
                      className="btn-soft-danger w-full"
                      onClick={async () => {
                        close();
                        const ok = await confirm({
                          title: `Delete ${selected.size} document${selected.size === 1 ? '' : 's'}?`,
                          body: 'They cannot be got back. Download them first if you are not sure.',
                          confirmLabel: 'Delete',
                          danger: true,
                        });
                        if (!ok) return;

                        for (const id of selected) await remove.mutateAsync(id);
                        setSelected(new Set());
                      }}
                    >
                      Delete the {selected.size} selected
                    </button>
                  )}
                </div>
              );
            }}
          </HeaderMenu>
        </div>
      </header>

      {isLoading ? (
        <p className="text-sm text-stone">Loading…</p>
      ) : documents.length === 0 ? (
        <div className="rounded-card border border-sand bg-white p-8 text-center">
          <Doodle name="paperclip" className="mx-auto mb-2 h-10 w-10 text-clay" />
          <p className="text-sm text-stone">
            Nothing filed yet. Add the forms and letters you print more than once — PDFs and images
            print straight from here, Word files come back down as files.
          </p>
        </div>
      ) : shown.length === 0 ? (
        <div className="space-y-1">
          <p className="text-sm text-stone">Nothing matches “{search}”.</p>
          {data?.stillReading && (
            <p className="text-xs text-stone">
              Some documents added before this search could look inside them are still being read.
              Try again in a moment.
            </p>
          )}
        </div>
      ) : (
        /*
         * Smaller, and more of them.
         *
         * The point of this page is finding one at a glance, and a wall of
         * large cards is a wall you scroll. Each one grows under the cursor
         * instead — big enough to read the heading of, without opening
         * anything.
         */
        <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
          {shown.map((doc) => (
            <li key={doc.id} className="relative">
              <DocumentCard
                document={doc}
                picked={selected.has(doc.id)}
                onPick={() => toggle(doc.id)}
                onOpen={() => setOpenId(doc.id)}
              />
            </li>
          ))}
        </ul>
      )}

      {open && <DocumentViewer key={open.id} document={open} onClose={() => setOpenId(null)} />}
    </div>
  );
}

/* --------------------------------------------------------------- a card */

function DocumentCard({
  document: doc,
  picked,
  onPick,
  onOpen,
}: {
  document: TeacherDocument;
  picked: boolean;
  onPick: () => void;
  onOpen: () => void;
}) {
  const update = useUpdateDocument();

  return (
    <div
      /*
        Grown from the middle, and lifted above its neighbours while it is.
        A card that grew in place would push the row about; one that grows
        without a z-index disappears under the next card along.
      */
      className={[
        'group relative overflow-hidden rounded-card border-2 bg-white',
        'transition-[transform,box-shadow,border-color] duration-150',
        'hover:z-20 hover:scale-[1.6] hover:shadow-lifted focus-within:z-20 focus-within:scale-[1.6]',
        picked ? 'border-terracotta' : 'border-sand hover:border-clay',
      ].join(' ')}
    >
      <button
        onClick={onOpen}
        className="block w-full text-left"
        aria-label={`Open ${doc.name}`}
      >
        {/*
          Page-shaped, and the whole page inside it.

          A fixed-height strip with the top of the document cropped into it is
          not a preview of the document — the bottom half of a permission slip
          is where the signature line lives. This is the proportions of a sheet
          of paper, with the page fitted inside.
        */}
        <span className="block aspect-[8.5/11] overflow-hidden border-b border-sand bg-white">
          <DocumentThumbnail document={doc} />
        </span>

        <span className="block px-2 py-1.5">
          <span className="block truncate text-sm text-espresso">{doc.name}</span>
          <span className="block truncate text-[11px] text-stone">
            {doc.snippet ? (
              // Why it matched. Not truncated in the middle of the word she
              // searched for — the snippet is already centred on it.
              <span className="italic">{doc.snippet}</span>
            ) : (
              doc.note || `${documentKind(doc.contentType)} · ${fileSizeLabel(doc.size)}`
            )}
          </span>
        </span>
      </button>

      {/* The picker for the bulk actions, out of the way in the corner. */}
      <label
        className="absolute left-1.5 top-1.5 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-sand bg-cream/90"
        title={picked ? 'Selected' : 'Select'}
      >
        <input type="checkbox" checked={picked} onChange={onPick} className="h-3.5 w-3.5" />
      </label>

      {/*
        The star, and only the star.

        Pinning is the one thing done while looking rather than after
        choosing, so it is the one thing that stays on the card. A filled star
        for pinned, an outline for not — no word needed for a control everybody
        already knows.
      */}
      <button
        onClick={() => update.mutate({ id: doc.id, pinned: !doc.pinned })}
        aria-label={doc.pinned ? `Unpin ${doc.name}` : `Pin ${doc.name}`}
        aria-pressed={doc.pinned}
        title={doc.pinned ? 'Kept at the top' : 'Keep at the top'}
        className={[
          'absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full border text-sm transition-colors',
          doc.pinned
            ? 'border-terracotta bg-cream text-terracotta'
            : 'border-sand bg-cream/90 text-stone opacity-0 hover:text-terracotta group-hover:opacity-100 focus:opacity-100',
        ].join(' ')}
      >
        {doc.pinned ? '★' : '☆'}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------- the card */

/**
 * The document, larger, and everything you can do with it.
 *
 * Print, download and delete live here rather than on the card: they are
 * decisions made about a particular document, and a wall of cards each
 * carrying four buttons is a wall of buttons rather than a wall of documents.
 */
function DocumentViewer({
  document: doc,
  onClose,
}: {
  document: TeacherDocument;
  onClose: () => void;
}) {
  const update = useUpdateDocument();
  const remove = useDeleteDocument();
  const confirm = useConfirm();
  const html = useDocumentHtml(doc);

  const [name, setName] = useState(doc.name);
  const [note, setNote] = useState(doc.note);
  const [printing, setPrinting] = useState(false);

  /*
   * A Word file prints once it has been converted, which is exactly what is
   * on screen behind this button — so the same page she is reading is the
   * page that comes out.
   */
  const printable =
    canPrintInBrowser(doc.contentType) || (isWordDocument(doc.contentType) && !!html.data?.converted);

  async function onPrint() {
    setPrinting(true);
    try {
      await printDocuments([
        { url: documentUrl(doc), contentType: doc.contentType, html: html.data?.html },
      ]);
    } finally {
      setPrinting(false);
    }
  }

  return (
    <Modal
      onClose={onClose}
      label={doc.name}
      className="flex h-[92vh] w-full max-w-5xl flex-col rounded-card bg-cream p-3 shadow-lifted"
    >
      {/*
        Tall enough for a whole page.

        A portrait page at a readable width is taller than it is wide, so a
        short box turns the reader into a scrolling strip — which is exactly
        what it did. The page fits itself to this, in both directions.
      */}
      <div className="mb-2 min-h-0 flex-1 overflow-hidden rounded-card border border-sand bg-white">
        {doc.contentType.startsWith('image/') ? (
          <img src={documentUrl(doc)} alt={doc.name} className="h-full w-full object-contain" />
        ) : doc.contentType === 'application/pdf' ? (
          <PdfReader url={documentUrl(doc)} className="h-full" />
        ) : (
          <WordReader document={doc} html={html.data} className="h-full" />
        )}
      </div>

      {/*
        Nothing between the document and the buttons.

        The name and the note used to sit here as two labelled fields, which
        made the panel look like a form with a document attached to it rather
        than a document. They are behind the gear now — renaming a file is a
        thing done once, and the reason to open this is to read what is in it.
      */}
      <div className="mt-3 flex shrink-0 flex-wrap items-center gap-2">
        {printable && (
          <button className="btn-primary" onClick={() => void onPrint()} disabled={printing}>
            {printing ? 'Preparing…' : 'Print'}
          </button>
        )}

        <a className={printable ? 'btn-secondary' : 'btn-primary'} href={documentDownloadUrl(doc)}>
          Download
        </a>

        <button
          className="btn-soft"
          onClick={() => update.mutate({ id: doc.id, pinned: !doc.pinned })}
          aria-pressed={doc.pinned}
        >
          {doc.pinned ? '★ Kept at the top' : '☆ Keep at the top'}
        </button>

        <button
          className="btn-soft-danger"
          onClick={async () => {
            const ok = await confirm({
              title: `Delete ${doc.name}?`,
              body: 'It cannot be got back. Download it first if you are not sure.',
              confirmLabel: 'Delete',
              danger: true,
            });
            if (ok) {
              remove.mutate(doc.id);
              onClose();
            }
          }}
        >
          Delete
        </button>

        <span className="ml-auto flex items-center gap-2">
          <HeaderMenu label="Rename this document" width={300}>
            <div className="space-y-2">
              <label className="block">
                <span className="label">Name</span>
                <input
                  className="field"
                  value={name}
                  maxLength={140}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => {
                    const trimmed = name.trim();
                    if (trimmed && trimmed !== doc.name) update.mutate({ id: doc.id, name: trimmed });
                    else setName(doc.name);
                  }}
                />
              </label>

              <label className="block">
                <span className="label">A note — what it is, or when it is due</span>
                <input
                  className="field"
                  value={note}
                  maxLength={300}
                  onChange={(e) => setNote(e.target.value)}
                  onBlur={() => {
                    if (note !== doc.note) update.mutate({ id: doc.id, note });
                  }}
                />
              </label>

              <p className="text-[11px] leading-snug text-stone">
                {documentKind(doc.contentType)} · {fileSizeLabel(doc.size)} · added{' '}
                {new Date(doc.uploadedAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
                {isWordDocument(doc.contentType) && ' · a Word file, read here and printable'}
              </p>
            </div>
          </HeaderMenu>

          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </span>
      </div>
    </Modal>
  );
}
